/**
 * What a paused agent's chat says about it: why it is paused, when it lifts, and what
 * the Resume button is called. Pure, so the banner and the list pill say one thing.
 */
import type { AgentWithContext } from "@/lib/types";

type Paused = Pick<AgentWithContext, "status" | "pausedReason" | "pausedUntil">;

export const pauseClock = (iso: string): string => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

/** "in 42 min", "in 3 h 5 min", "in 2 days"; "now" once it has passed. */
export function untilText(iso: string, now: number): string {
  const minutes = Math.ceil((Date.parse(iso) - now) / 60_000);
  if (!Number.isFinite(minutes) || minutes <= 0) return "now";
  if (minutes < 60) return `in ${minutes} min`;
  if (minutes < 48 * 60) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return `in ${hours} h${rest ? ` ${rest} min` : ""}`;
  }
  return `in ${Math.round(minutes / (24 * 60))} days`;
}

export interface PauseSummary {
  /** The banner's headline. */
  title: string;
  /** What lifts it, said under the title; null when the title says it all. */
  detail: string | null;
  /** The button: a limit pause can be lifted early ("Resume now"). */
  action: string;
  /** The list row's pill, short. */
  pill: string;
}

/** Null for an agent that is not paused. */
export function pauseSummary(agent: Paused, now: number, clock: (iso: string) => string = pauseClock): PauseSummary | null {
  if (agent.status !== "stopped") return null;
  if (agent.pausedReason === "limit") {
    const until = agent.pausedUntil;
    const passed = !until || Date.parse(until) <= now;
    return {
      title: passed || !until ? "Paused: usage limit, resuming shortly" : `Paused: usage limit, resumes at ${clock(until)} (${untilText(until, now)})`,
      detail: "It resumes by itself when the limit resets. Resume now gives it 3 fresh tries.",
      action: "Resume now",
      pill: until && !passed ? `paused · ${clock(until)}` : "paused · limit",
    };
  }
  if (agent.pausedReason === "sign-in") {
    return {
      title: "Paused: sign-in refused",
      detail: "Switch the Claude account in Settings, then resume.",
      action: "Resume",
      pill: "paused · sign-in",
    };
  }
  return { title: "Paused by you", detail: null, action: "Resume", pill: "paused" };
}

/** "3 messages waiting", "1 message waiting", "Nothing waiting". */
export const waitingText = (count: number): string =>
  count === 0 ? "Nothing waiting" : `${count} ${count === 1 ? "message" : "messages"} waiting`;
